<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	//Formatters
	import { format } from 'src/services/i18n';
	//Design System Components
	import {
		Modal,
		Container,
		CircleAvatar,
		Alert,
		Text,
		Collapse,
		ListItem,
		Button,
	} from '@Shelly/shelly-design-system/index';
	//Types
	import type { Device } from 'src/types/Device';
	import type { DeviceUsage } from './device_usage_helper';
	//Helpers
	import { deleteDevice, deleteSharedDevice } from 'src/helpers/add_delete_helpers';
	import { isBluTRV, isWallDisplay, real_id } from 'src/helpers/interface_helpers';
	import { deleteVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { deleteWallDisplayExtention } from 'src/helpers/wall_display_extract/extracting_helpers';
	import { deleteBluTRV } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { deletePeripheralDevice, isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	import { deleteIRDevice, isIRDevice } from 'src/helpers/ir_controller_helpers';
	import { getDeviceAvatarDefaultConfig } from 'src/helpers/device_state_view_helpers';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';

	export let device: Device;
	export let showModalDelete = false;
	export let deviceIsUsed = false;
	export let deviceUsage: DeviceUsage = {
		room: [],
		group: [],
		alarm: [],
		scene: [],
		dashboard: [],
		thermostat: [],
	};

	const dispatch = createEventDispatcher();
	let deleteLoading = false;
	let expandedCollapse = false;

	$: modalTitle = device.shared
		? $format('prompt_title_remove_device', { default: 'Remove device?' })
		: $format('prompt_title_delete_device', { default: 'Delete device?' });

	$: modalDescription = device.shared
		? $format('remove_device_msg', {
				values: { device_name: device.name },
				default: `Removing device '{device_name}' will permanently remove it from your account. You can regain access if the owner shares the device with you again.`,
			})
		: $format('delete_device_msg', {
				values: { device_name: device.name },
				default: `Deleting device '{device_name}' will permanently remove it from your account. Once deleted, you'll need to add it again to restore access.`,
			});

	$: primaryButtonText = device.shared
		? $format('button_remove_device', { default: 'Remove device' })
		: $format('button_delete_device', { default: 'Delete device' });

	$: deleteDeviceAlertHeading = !deviceIsUsed
		? $format('safely_delete_device_msg', {
				default:
					'Currently, this device is not used anywhere within the app. You can safely delete it.',
			})
		: $format('delete_device_will_remove_it_msg', {
				default:
					'If you delete the device, it will be automatically removed from all places where it is currently in use. This device is currently used in the following places:',
			});

	$: showWallDisplayFactoryResetWarning =
		isWallDisplay(device.type) && !device.shared && !device.id.includes('_');

	const onFinishCardDelete = async () => {
		const _id = real_id(device.id);
		const sidePanelState = $sidePanelStateCombined;
		if (sidePanelState?.type === 'device' && sidePanelState?.id?.startsWith(_id)) resetDrawerClose();

		deleteLoading = true;
		dispatch('deleteLoading', true);
		if (device.shared) {
			await deleteSharedDevice(device.id);
		} else if (isBluTRV(device.type)) {
			await deleteBluTRV(device);
		} else if (isIRDevice(device.type)) {
			await deleteIRDevice(device);
		} else if (device.channel >= 200) {
			//Delete virtual device
			await deleteVirtualDevice(device);
		} else if (isPeripheralDevice(device.type)) {
			//Delete peripheral device
			await deletePeripheralDevice(device);
		} else if (isWallDisplay(device.type)) {
			await deleteWallDisplayExtention(device);
		} else {
			await deleteDevice(device.id);
		}
		deleteLoading = false;
		dispatch('deleteLoading', false);

		device = {} as any;
		showModalDelete = false;
	};
</script>

<Modal on:close={() => (showModalDelete = false)} showModal={showModalDelete} title={modalTitle}>
	<Container slot="content" padding="0">
		<div class="d-flex justify-content-center mb-m">
			<CircleAvatar
				{...getDeviceAvatarDefaultConfig(device, 'mb-0')}
				borderStatus={{ icon: 'fa-solid fa-trash', color: 'color-danger' }}
			/>
		</div>
		<Text class="text-bold text-center mb-s">
			‘{device.name}’
		</Text>
		<Text class="mb-m" styleType="controls-large">
			{modalDescription}
		</Text>
		<Alert
			class={(showWallDisplayFactoryResetWarning || deviceIsUsed) && 'mb-m'}
			heading={deleteDeviceAlertHeading}
			type={deviceIsUsed ? 'warning' : 'info'}
		/>
		{#if showWallDisplayFactoryResetWarning}
			<Alert
				class={deviceIsUsed && 'mb-m'}
				type="warning"
				heading={$format('delete_wall_display_factory_reset_alert_msg', {
					default: 'Deleting this device will reset it to its factory settings.',
				})}
			/>
		{/if}
		{#if deviceIsUsed}
			<Collapse
				id="collapse-3xilfq1"
				title={$format('device_usage_title', { default: 'Usage of the device' })}
				expanded={expandedCollapse}
				on:click={() => {
					expandedCollapse = !expandedCollapse;
				}}
			>
				<div class="d-flex flex-direction-column" style="gap: var(--spacing-s)">
					{#each Object.entries(deviceUsage) as [category, items]}
						{#if Array.isArray(items)}
							{#each items as item}
								<ListItem
									icon="fa-solid fa-door-open"
									viewType="default"
									title={item?._meta?.name || item?.name || item}
									subtitle="({capFirstChar(
										$format(`label_left_badge_${category}`, {
											default: category,
										}),
									)})"
								/>
							{/each}
						{/if}
					{/each}
				</div>
			</Collapse>
		{/if}
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={() => (showModalDelete = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			loading={deleteLoading}
			on:click={onFinishCardDelete}
		>
			{primaryButtonText}
		</Button>
	</div>
</Modal>
